import * as React from 'react'
import { useState, useEffect } from 'react'
import {
    IconButtonSeeker,
    useToaster,
    GridSeeker,
    SelectSeeker,
    ModalSeeker,
    IconSeekers,
    SubmitButtonSeeker,
    SwitchSeeker,
    DateRangePickerSeeker
} from 'ui-seeker/core'
import { postSubmit, formatDate, FORMAT_DATE_BACK } from 'ui-seeker/utils'
import CreatableSelect from 'react-select/creatable'
import FormHelperText from "@material-ui/core/FormHelperText"

import { validateEmail, getSection } from 'app/utils'
import { useCommonFilters } from 'app/components'
import { getAppUserData } from "app/@utils";
import { AppTooltip } from 'app/@components'
import { COMPETITION_URL, PARITY_URL, PACKAGES_URL } from 'app/@common/constants'


const DATE_RANGE_URLS = {
    competition: COMPETITION_URL.DATE_RANGE,
    parity: PARITY_URL.DATE_RANGE,
    packages: PACKAGES_URL.DATE_RANGE,
}


export default function ExcelBtn() {

    const createOption = label => ({
        label,
        value: label,
    })

    const { email, priceByNight } = getAppUserData()
    const [loading, setLoading] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [emails, setEmails] = useState([createOption(email)])
    const [emailsOK, setEmailsOK] = useState(true)
    const [submit, setSubmit] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [monthOptions, setMonthOptions] = useState([])
    const [selectedMonths, setSelectedMonths] = useState([])
    const [useCustomRange, setUseCustomRange] = useState(false)
    const [customRange, setCustomRange] = useState([null, null])
    const [maxDate, setMaxDate] = useState(null)
    const [loadingOptions, setLoadingOptions] = useState(false)
    const { showToaster } = useToaster()
    const { dataFilters, dateRange, customDateRange, mainHotels } = useCommonFilters()

    const isAsync = selectedMonths.length >= 3 || useCustomRange

    const handleCloseModal = () => {
        setOpenModal(false)
        setSelectedMonths([])
        setUseCustomRange(false)
        setCustomRange([null, null])
        setEmails([createOption(email)])
        setEmailsOK(true)
        setInputValue('')
    }

	const downloadFile = fileUrl => {
		const link = document.createElement('a')
		link.href = fileUrl
		link.download = fileUrl
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

    useEffect(() => {
        if (!openModal) return

        const section = getSection(window.location.href)
        const url = DATE_RANGE_URLS[section]
        if (!url) return

        setLoadingOptions(true)
        postSubmit(url, { mainHotels }).then(options => {
            const customOption = options.find(opt => opt.value === 'custom')
            const months = options.filter(opt => opt.value !== 'custom')

            setMonthOptions(months)
            if (customOption?.lastDate) setMaxDate(new Date(customOption.lastDate))

            if (dateRange === 'custom') {
                setUseCustomRange(true)
                setSelectedMonths([])
                if (customDateRange?.[0] && customDateRange?.[1]) {
                    setCustomRange([new Date(customDateRange[0]), new Date(customDateRange[1])])
                }
            } else {
                const currentMonth = months.find(m => m.value === dateRange)
                setSelectedMonths(currentMonth ? [currentMonth] : months.length ? [months[0]] : [])
            }
        }).catch(() => {
            showToaster.error('Error loading date options')
        }).finally(() => {
            setLoadingOptions(false)
        })
    }, [openModal])

    const buildDateParams = () => {
        if (useCustomRange) {
            return {
                dateRange: 'custom',
                startDate: formatDate(customRange[0], { format: FORMAT_DATE_BACK }),
                endDate: formatDate(customRange[1], { format: FORMAT_DATE_BACK }),
            }
        }
        if (selectedMonths.length === 1) {
            return { dateRange: selectedMonths[0].value }
        }
        return { dateRange: selectedMonths.map(m => m.value) }
    }

    const getData = () => {
        setLoading(true)
        showToaster.info('Please wait...')

        const urlSection = window.location.href.includes('competition') ? 'competition' : (window.location.href.includes('parity') ? 'parity' : 'packages')
        const url = `/api/${urlSection}/export/excel/async`
        console.log(priceByNight)

        postSubmit(url, {
            ...dataFilters,
            ...buildDateParams(),
            ...isAsync && emails && { session_data: { user_email: emails.map(a => a.value), async: true } },
            nightly: (/true/).test(priceByNight)
        }).then(res => {
            if (isAsync) {
                showToaster.success('Exporting... we will email your export as soon as it\'s ready!')
            } else {
                showToaster.success('Downloading Excel file...')
                downloadFile(res.storage_url)
            }
        }).catch(e => {
            showToaster.error(e.status === 400 ? 'Error sending mail. Are you sure you have your email assigned?' : 'Error processing file. Try again!')
        }).finally(() => {
            handleCloseModal()
            setLoading(false)
            setSubmit(false)
        })
    }

    useEffect(() => {
        if (!submit) return
        if (isAsync) {
            if (emails.length) {
                const hasInvalid = emails.some(a => !validateEmail(a.value))
                if (hasInvalid) {
                    setSubmit(false)
                    setEmailsOK(false)
                    return
                }
                setEmailsOK(true)
                getData()
            } else {
                setSubmit(false)
                setEmailsOK(false)
            }
        } else {
            getData()
        }
    }, [submit])

    const handleKeyDown = event => {
        if (!inputValue) return;

        switch (event.key) {
            case 'Enter':
            case 'Tab':
                setEmails(prev => [...prev, createOption(inputValue)])
                setInputValue('')
                event.preventDefault()
                break;
            default:
                break;
        }

        if (event.type === 'blur') {
            setEmails(prev => [...prev, createOption(inputValue)])
            setInputValue('')
            event.preventDefault()
        }
    }

    const modalConfig = {
        isOpen: openModal,
        controller: handleCloseModal,
        title: 'Download Excel',
        okBtn: false,
        cancelBtn: false
    }

    const configTagInput = {
        components: { DropdownIndicator: null }, inputValue: inputValue, isClearable: true, isMulti: true, 
        menuIsOpen: false, placeholder: "Insert emails", value: emails,
        onChange: newValue => setEmails(newValue), 
        onInputChange: newValue => setInputValue(newValue),
        onKeyDown: handleKeyDown, onMouseOver: handleKeyDown, onBlur: handleKeyDown, 
    }

    return <>
        <AppTooltip title="Export to Excel">
            <IconButtonSeeker
                icon="file-excel"
                iconType="fontAwesome"
                className="icon-filter-excel-custom"
                onClick={() => setOpenModal(true)}
            />
        </AppTooltip>

        <ModalSeeker {...modalConfig}>
            <GridSeeker item xs={12}>
                <SelectSeeker
                    isMulti
                    label="Select months"
                    options={monthOptions}
                    value={selectedMonths.map(m => m.value)}
                    onChange={setSelectedMonths}
                    disabled={useCustomRange}
                    isLoading={loadingOptions}
                    closeMenuOnSelect={false}
                />
            </GridSeeker>
            <GridSeeker item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
                    <SwitchSeeker
                        checked={useCustomRange}
                        onChange={e => {
                            const checked = e.target.checked
                            setUseCustomRange(checked)
                            if (checked) setSelectedMonths([])
                        }}
                    />
                    <span style={{ marginLeft: '-15px' }}>Custom date range</span>
                </div>
            </GridSeeker>
            {useCustomRange && <GridSeeker item xs={12}>
                <DateRangePickerSeeker
                    value={customRange[0] && customRange[1] ? customRange : []}
                    onChange={range => setCustomRange(range || [null, null])}
                    disabledDate={maxDate ? d => d > maxDate : null}
                    label="Date range"
                    cleanable
                />
            </GridSeeker>}
            {isAsync && <GridSeeker item xs={12}>
                <fieldset className="selectSeeker-wrapper" style={{ borderRadius: '4px', marginTop: 8 }}>
                    <legend>Emails</legend>
                    <label>Emails *</label>
                    <div className="MuiInputBase-root MuiInputBase-fullWidth MuiInputBase-formControl MuiInputBase-adornedStart MuiOutlinedInput-adornedStart container-creatable">
                        <IconSeekers icon='envelope' iconType='fontAwesome' iconWeight='fal' />
                        <CreatableSelect {...configTagInput} />
                    </div>
                </fieldset>
                <span style={{ fontSize: '10px', color: 'lightgrey', marginTop: 4 }}>Due to the size of the excel it will be sent by email</span>
                {!emailsOK && <FormHelperText style={{ color: 'red' }}>Please insert emails correctly!</FormHelperText>}
            </GridSeeker>}
            <GridSeeker className="flex flex-end modal-excel-footer" size={12}>
                <SubmitButtonSeeker className={`modal-btn bg-secondary`} onClick={() => {
                    if (useCustomRange && (!customRange[0] || !customRange[1])) return
                    if (!useCustomRange && selectedMonths.length === 0) return
                    setSubmit(true)
                }} isLoading={loading} style={{ margin: '10px 0 0 0' }}>
                    OK
                </SubmitButtonSeeker>
            </GridSeeker>
        </ModalSeeker>
    </>
}